import {useLayoutEffect, useRef, useState, type FormEvent} from "react";
import {Navigate, useLocation, useNavigate} from "react-router-dom";
import {useCabinetData} from "@/hooks/useCabinetData";

const MAX_REASON_LENGTH = 600;
const DESKTOP_KEEP_BUTTON_BOTTOM_GAP = 28;

export function CabinetCancelReasonPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const {subscription, paid, isLoading, isError, refetch} = useCabinetData();
    const keepSubscriptionRef = useRef<HTMLButtonElement>(null);
    const [reason, setReason] = useState(
        () => ((location.state as {reason?: string} | null)?.reason ?? ""),
    );
    const normalizedReason = reason.trim();

    useLayoutEffect(() => {
        if (window.matchMedia("(max-width: 620px)").matches) {
            window.scrollTo({top: 0, left: 0, behavior: "auto"});
            return;
        }
        const keepSubscriptionButton = keepSubscriptionRef.current;
        if (isLoading || !paid || !subscription || !keepSubscriptionButton) {
            return;
        }

        let disposed = false;
        const positionSaveButton = () => {
            if (disposed) {
                return;
            }
            const buttonBottom = keepSubscriptionButton.getBoundingClientRect().bottom + window.scrollY;
            window.scrollTo({
                top: Math.max(0, buttonBottom - window.innerHeight + DESKTOP_KEEP_BUTTON_BOTTOM_GAP),
                left: 0,
                behavior: "auto",
            });
        };
        const frame = window.requestAnimationFrame(positionSaveButton);
        void document.fonts.ready.then(positionSaveButton);
        return () => {
            disposed = true;
            window.cancelAnimationFrame(frame);
        };
    }, [isLoading, paid, subscription]);

    if (isLoading) {
        return (
            <div className="cabinet-state">
                <h2>Загружаем подписку</h2>
                <p>Проверяем статус перед изменением автопродления.</p>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="cabinet-state">
                <h2>Не удалось загрузить подписку</h2>
                <button type="button" className="btn btn-primary" onClick={() => void refetch()}>
                    Повторить
                </button>
            </div>
        );
    }

    if (!paid || !subscription) {
        return <Navigate to="/subscribe/" replace/>;
    }

    if (subscription.cancel_at_period_end) {
        return <Navigate to="/cabinet/subscription/cancelled" replace/>;
    }

    const continueCancellation = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!normalizedReason) {
            return;
        }
        navigate("/cabinet/subscription/cancel/freeze", {state: {reason: normalizedReason}});
    };

    return (
        <section className="cabinet-cancel-flow" aria-labelledby="cancel-reason-title">
            <button
                type="button"
                className="cabinet-cancel-back"
                onClick={() => navigate(-1)}
            >
                <span aria-hidden="true">←</span>
                Назад
            </button>

            <article className="cabinet-panel cabinet-cancel-card">
                <div className="cabinet-cancel-heading">
                    <span className="cabinet-cancel-eyebrow">Отмена подписки</span>
                    <h2 id="cancel-reason-title">Почему вы хотите отменить подписку?</h2>
                    <p>
                        Ваш ответ поможет нам сделать Лидхолд полезнее. Пожалуйста,
                        опишите причину — без неё отменить подписку не получится.
                    </p>
                </div>

                <form className="cabinet-cancel-form" onSubmit={continueCancellation}>
                    <label className="cabinet-cancel-field" htmlFor="cancellation-reason">
                        <span>Причина отмены</span>
                        <textarea
                            id="cancellation-reason"
                            value={reason}
                            maxLength={MAX_REASON_LENGTH}
                            rows={6}
                            required
                            placeholder="Например: не хватает нужной функции или сейчас сервис не вписывается в бюджет"
                            aria-describedby="cancellation-reason-hint"
                            onChange={(event) => setReason(event.target.value)}
                        />
                    </label>
                    <div id="cancellation-reason-hint" className="cabinet-cancel-field-meta">
                        <span>Опишите причину своими словами</span>
                        <span>{reason.length} / {MAX_REASON_LENGTH}</span>
                    </div>

                    <div className="cabinet-cancel-actions">
                        <button
                            ref={keepSubscriptionRef}
                            type="button"
                            className="btn btn-primary cabinet-cancel-keep"
                            onClick={() => navigate("/cabinet")}
                        >
                            Сохранить подписку
                        </button>
                    </div>

                    <div className="cabinet-cancel-final-step">
                        <button
                            type="submit"
                            className="btn cabinet-cancel-confirm"
                            disabled={!normalizedReason}
                        >
                            Отменить подписку
                        </button>
                    </div>
                </form>
            </article>
        </section>
    );
}
